<script setup lang="ts" generic="T extends SelectableValue">
import type { FormFieldProps, FormFieldSlots } from '@/components/formField/FormField';
import type { Selectable, SelectableValue, SelectBoxProps } from '@/components/selectBox/selectBox';

import { useFormField } from '@/components/formField/useFormField';

import FormField from '@/components/formField/FormField.vue';
import Listbox from '@/components/selectBox/listbox/ListboxInput.vue';
import MobileListboxInput from '@/components/selectBox/listbox/MobileListboxInput.vue';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

type FormComboboxProps = SelectBoxProps<T> & FormFieldProps;

const props = defineProps<FormComboboxProps>();
defineSlots<FormFieldSlots>();

const emit = defineEmits<{
  'update:modelValue': [Selectable<T> | null];
}>();

function onValueChange(value: Selectable<T> | null): void {
  emit('update:modelValue', value);
}

const { formFieldProps, slots } = useFormField({ props });

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMdAndAbove = breakpoints.greaterOrEqual('md');
</script>

<template>
  <FormField v-bind="formFieldProps">
    <Listbox
      v-if="isMdAndAbove"
      :hasError="formFieldProps.hasError"
      :iconLeft="iconLeft"
      :isDisabled="isDisabled"
      :isOptional="isOptional"
      :modelValue="modelValue as Selectable<T>"
      :options="options"
      :placeholder="placeholder"
      @update:modelValue="onValueChange"
    />
    <MobileListboxInput
      v-else
      :hasError="formFieldProps.hasError"
      :iconLeft="iconLeft"
      :isDisabled="isDisabled"
      :isOptional="isOptional"
      :label="label"
      :modelValue="modelValue as Selectable<T>"
      :options="options"
      :placeholder="placeholder"
      @update:modelValue="onValueChange"
    />
    <template v-for="slot of slots" #[slot]>
      <slot :name="slot" />
    </template>
  </FormField>
</template>
